import type { Question, CompetencyCode } from '../types'

import { type OrderingSeed, type SelectionSeed, type GraphSeed, generateOrderingSeed, generateSelectionSeed, generateGraphSeed } from './generators/logic_generators'

const logicMeta: Pick<Question, 'exam' | 'domain' | 'knowledge' | 'prerequisites' | 'misconceptionOptions'> = {
  exam: 'VACT',
  domain: 'Logic',
  knowledge: ['logic'],
  prerequisites: ['logic'],
  misconceptionOptions: ['condition-reversal', 'strategy-choice', 'careless-reading', 'time-pressure'],
}

const orderingCompetency: Question['competency'] = ['logical-reasoning', 'pattern-recognition']
const selectionCompetency: Question['competency'] = ['logical-reasoning', 'decision-making']
const graphCompetency: Question['competency'] = ['logical-reasoning', 'problem-solving']

const orderingSeeds: OrderingSeed[] = [
  { id: 'task', title: 'Năm nhiệm vụ trong ngày', first: 'Khởi động', second: 'Thu thập dữ liệu', third: 'Kiểm tra thiết bị', fourth: 'Báo cáo', fifth: 'Thiết lập an toàn' },
  { id: 'talk', title: 'Năm tiết mục hội thảo', first: 'Mở đầu', second: 'Chuyên đề Toán', third: 'Chuyên đề Khoa học', fourth: 'Tổng kết', fifth: 'Giới thiệu diễn giả' },
  { id: 'robot', title: 'Năm lệnh của robot', first: 'Quét mã', second: 'Đọc cảm biến', third: 'Kiểm tra pin', fourth: 'Gửi kết quả', fifth: 'Khởi tạo hệ thống' },
  { id: 'experiment', title: 'Năm bước thí nghiệm', first: 'Chuẩn bị mẫu', second: 'Đo lần một', third: 'Đo lần hai', fourth: 'Kết luận', fifth: 'Hiệu chuẩn dụng cụ' },
]

const selectionSeeds: SelectionSeed[] = [
  { id: 'library', title: 'Chọn tổ trực thư viện', a: 'An', b: 'Bình', c: 'Chi', d: 'Dũng', e: 'Hà' },
  { id: 'contest', title: 'Chọn đội thi học thuật', a: 'Khoa', b: 'Lan', c: 'Minh', d: 'Ngọc', e: 'Phúc' },
  { id: 'camp', title: 'Chọn tổ hậu cần cắm trại', a: 'Quân', b: 'Trang', c: 'Uyên', d: 'Việt', e: 'Xuân' },
]

const graphSeeds: GraphSeed[] = [
  { id: 'island', title: 'Bản đồ năm đảo', p: 'Đảo A', q: 'Đảo B', r: 'Đảo C', s: 'Đảo D', t: 'Đảo E' },
  { id: 'station', title: 'Mạng năm ga', p: 'Ga P', q: 'Ga Q', r: 'Ga R', s: 'Ga S', t: 'Ga T' },
  { id: 'room', title: 'Mạng năm phòng học', p: 'Phòng 1', q: 'Phòng 2', r: 'Phòng 3', s: 'Phòng 4', t: 'Phòng 5' },
]

const option = (id: string, text: string) => ({ id, text })

export function orderingQuestions(seed: OrderingSeed): Question[] {
  const passageText = `Năm mục ${seed.first}, ${seed.second}, ${seed.third}, ${seed.fourth}, ${seed.fifth} được xếp vào 5 vị trí theo thứ tự. ${seed.first} diễn ra trước ${seed.second} và ${seed.third}. ${seed.second} và ${seed.third} đều diễn ra trước ${seed.fourth}. ${seed.fifth} diễn ra ngay sau ${seed.first}.`
  const base = {
    ...logicMeta,
    topic: 'Sắp xếp tuyến tính',
    competency: ['logical-reasoning', 'pattern-recognition'] as CompetencyCode[],
    passageTitle: seed.title,
    passageText,
    tags: ['original', 'authored-variant', 'logic', 'ordering'],
    aiContext: JSON.stringify(seed),
  }
  return [
    {
      ...base,
      id: `VACT-LOGIC-AUTH-ORD-${seed.id}-01`, difficulty: 2, cognitiveLevel: 'M2', type: 'single-choice',
      stem: 'Mục nào chắc chắn ở vị trí 1?',
      choices: [option('A', seed.first), option('B', seed.second), option('C', seed.third), option('D', seed.fifth)], answer: 'A',
      solution: `${seed.first} phải đứng trước ${seed.second}, ${seed.third}, ${seed.fourth} và ${seed.fifth}; đồng thời ${seed.fifth} đứng ngay sau ${seed.first}. Vì vậy không thể có mục nào trước ${seed.first}, nên ${seed.first} ở vị trí 1.`,
      hint1: `Đếm số mục buộc phải đứng sau ${seed.first}.`,
      hint2: `${seed.first} đứng trước ba mục và còn kéo theo ${seed.fifth} ngay sau nó.`, estimatedSeconds: 90,
      variantGroup: 'logic-authored-order-chain-v1',
    },
    {
      ...base,
      id: `VACT-LOGIC-AUTH-ORD-${seed.id}-02`, difficulty: 2, cognitiveLevel: 'M2', type: 'single-choice',
      stem: 'Mục nào chắc chắn ở vị trí 5?',
      choices: [option('A', seed.second), option('B', seed.third), option('C', seed.fourth), option('D', seed.fifth)], answer: 'C',
      solution: `${seed.second}, ${seed.third} và ${seed.fifth} đều phải đứng trước ${seed.fourth}: hai mục đầu theo điều kiện trực tiếp, còn ${seed.fifth} ngay sau ${seed.first} mà ${seed.first} đứng trước ${seed.second} và ${seed.third}. Do đó ${seed.fourth} phải ở vị trí cuối.`,
      hint1: `Xem những mục nào buộc phải xuất hiện trước ${seed.fourth}.`,
      hint2: `${seed.second} và ${seed.third} trước ${seed.fourth}; ${seed.first}, ${seed.fifth} cũng phải nằm trước hai mục đó hoặc kề ngay sau.`, estimatedSeconds: 95,
      variantGroup: 'logic-authored-order-chain-v1',
    },
    {
      ...base,
      id: `VACT-LOGIC-AUTH-ORD-${seed.id}-03`, difficulty: 3, cognitiveLevel: 'M2', type: 'single-choice',
      stem: 'Cặp mục nào có thể chiếm hai vị trí 3 và 4 (không xét thứ tự trong cặp)?',
      choices: [option('A', `${seed.first} và ${seed.fifth}`), option('B', `${seed.second} và ${seed.third}`), option('C', `${seed.second} và ${seed.fourth}`), option('D', `${seed.third} và ${seed.fourth}`)], answer: 'B',
      solution: `${seed.first} ở vị trí 1 và ${seed.fifth} ở vị trí 2; ${seed.fourth} ở vị trí 5. Hai vị trí 3, 4 còn lại dành cho ${seed.second} và ${seed.third}, theo bất kỳ thứ tự nào.`,
      hint1: 'Tìm hai vị trí bị cố định trước, rồi tìm vị trí cuối.',
      hint2: `Sau ${seed.first}, ${seed.fifth} phải đứng ngay sau; ${seed.fourth} đứng sau cả ${seed.second} lẫn ${seed.third}.`, estimatedSeconds: 110,
      variantGroup: 'logic-authored-order-chain-v1',
    },
  ]
}

export function selectionQuestions(seed: SelectionSeed): Question[] {
  const passageText = `Chọn đúng 3 bạn từ ${seed.a}, ${seed.b}, ${seed.c}, ${seed.d}, ${seed.e}. ${seed.a} và ${seed.b} hoặc cùng được chọn, hoặc cùng không được chọn. Nếu ${seed.c} được chọn thì ${seed.d} không được chọn. Nếu ${seed.e} được chọn thì ${seed.c} cũng được chọn.`
  const base = {
    ...logicMeta,
    topic: 'Phân bổ có điều kiện',
    competency: ['logical-reasoning', 'decision-making'] as CompetencyCode[],
    passageTitle: seed.title,
    passageText,
    tags: ['original', 'authored-variant', 'logic', 'selection'],
    aiContext: JSON.stringify(seed),
  }
  return [
    {
      ...base,
      id: `VACT-LOGIC-AUTH-SEL-${seed.id}-01`, difficulty: 2, cognitiveLevel: 'M2', type: 'single-choice',
      stem: 'Bạn nào chắc chắn được chọn?',
      choices: [option('A', seed.a), option('B', seed.c), option('C', seed.d), option('D', seed.e)], answer: 'A',
      solution: `Nếu ${seed.a}, ${seed.b} cùng không được chọn thì để đủ 3 người phải chọn ${seed.c}, ${seed.d}, ${seed.e}, nhưng ${seed.c} và ${seed.d} không thể cùng chọn. Vậy ${seed.a}, ${seed.b} đều phải được chọn; do đó ${seed.a} chắc chắn được chọn.`,
      hint1: `Thử giả sử ${seed.a} và ${seed.b} cùng không được chọn.`,
      hint2: `Khi đó chỉ còn ${seed.c}, ${seed.d}, ${seed.e}, nhưng ${seed.c} loại ${seed.d}.`, estimatedSeconds: 100,
      variantGroup: 'logic-authored-selection-pair-v1',
    },
    {
      ...base,
      id: `VACT-LOGIC-AUTH-SEL-${seed.id}-02`, difficulty: 2, cognitiveLevel: 'M2', type: 'single-choice',
      stem: 'Bạn nào không thể được chọn?',
      choices: [option('A', seed.a), option('B', seed.b), option('C', seed.c), option('D', seed.e)], answer: 'D',
      solution: `${seed.a} và ${seed.b} bắt buộc cùng có mặt. Nếu chọn thêm ${seed.e}, điều kiện ${seed.e} ⇒ ${seed.c} sẽ đòi thêm ${seed.c}, thành bốn người. Vì vậy ${seed.e} không thể được chọn.`,
      hint1: `Trước hết xác định cặp bắt buộc ${seed.a}, ${seed.b}.`,
      hint2: `${seed.e} kéo theo ${seed.c}; khi đó số người sẽ vượt quá 3.`, estimatedSeconds: 95,
      variantGroup: 'logic-authored-selection-pair-v1',
    },
    {
      ...base,
      id: `VACT-LOGIC-AUTH-SEL-${seed.id}-03`, difficulty: 2, cognitiveLevel: 'M2', type: 'single-choice',
      stem: `Nếu ${seed.c} được chọn, ai sau đây không thể được chọn cùng ${seed.c}?`,
      choices: [option('A', seed.a), option('B', seed.b), option('C', seed.d), option('D', seed.e)], answer: 'C',
      solution: `Điều kiện cho trực tiếp: nếu ${seed.c} được chọn thì ${seed.d} không được chọn. Nhóm hợp lệ khi đó là ${seed.a}, ${seed.b}, ${seed.c}.`,
      hint1: `Tìm điều kiện có ${seed.c} ở vế “nếu”.`,
      hint2: `${seed.c} ⇒ không ${seed.d}.`, estimatedSeconds: 75,
      variantGroup: 'logic-authored-selection-pair-v1',
    },
    {
      ...base,
      id: `VACT-LOGIC-AUTH-SEL-${seed.id}-04`, difficulty: 3, cognitiveLevel: 'M2', type: 'single-choice',
      stem: 'Có bao nhiêu nhóm 3 bạn hợp lệ?',
      choices: [option('A', '1'), option('B', '2'), option('C', '3'), option('D', '4')], answer: 'B',
      solution: `Cặp ${seed.a}, ${seed.b} bắt buộc được chọn. Người thứ ba chỉ có thể là ${seed.c} hoặc ${seed.d}; không thể là ${seed.e} vì ${seed.e} kéo theo ${seed.c}. Vì vậy chỉ có hai nhóm: {${seed.a}, ${seed.b}, ${seed.c}} và {${seed.a}, ${seed.b}, ${seed.d}}.`,
      hint1: 'Xác định cặp bắt buộc trước, rồi xét người thứ ba.',
      hint2: `${seed.e} không thể là người thứ ba vì sẽ kéo theo thêm ${seed.c}.`, estimatedSeconds: 115,
      variantGroup: 'logic-authored-selection-pair-v1',
    },
  ]
}

export function graphQuestions(seed: GraphSeed): Question[] {
  const passageText = `Có 5 điểm ${seed.p}, ${seed.q}, ${seed.r}, ${seed.s}, ${seed.t}. Các đường đi hai chiều là: ${seed.p}–${seed.q}, ${seed.q}–${seed.r}, ${seed.r}–${seed.s}, ${seed.s}–${seed.t}, ${seed.q}–${seed.t}; không có đường nào khác.`
  const base = {
    ...logicMeta,
    topic: 'Sơ đồ và đường đi',
    competency: ['logical-reasoning', 'problem-solving'] as CompetencyCode[],
    passageTitle: seed.title,
    passageText,
    tags: ['original', 'authored-variant', 'logic', 'graph'],
    aiContext: JSON.stringify(seed),
  }
  return [
    {
      ...base,
      id: `VACT-LOGIC-AUTH-GRAPH-${seed.id}-01`, difficulty: 2, cognitiveLevel: 'M2', type: 'single-choice',
      stem: `Nếu đóng ${seed.q}, điểm nào bị tách khỏi tất cả các điểm còn lại?`,
      choices: [option('A', seed.p), option('B', seed.r), option('C', seed.s), option('D', seed.t)], answer: 'A',
      solution: `${seed.p} chỉ nối trực tiếp với ${seed.q}. Khi ${seed.q} đóng, ${seed.p} không còn đường nào đi tới mạng còn lại.`,
      hint1: `Đếm các đường nối trực tiếp từ ${seed.p}.`,
      hint2: `${seed.p} chỉ có một láng giềng là ${seed.q}.`, estimatedSeconds: 80,
      variantGroup: 'logic-authored-graph-branch-v1',
    },
    {
      ...base,
      id: `VACT-LOGIC-AUTH-GRAPH-${seed.id}-02`, difficulty: 2, cognitiveLevel: 'M2', type: 'single-choice',
      stem: `${seed.q} nối trực tiếp với bao nhiêu điểm?`,
      choices: [option('A', '1'), option('B', '2'), option('C', '3'), option('D', '4')], answer: 'C',
      solution: `${seed.q} nối trực tiếp với ${seed.p}, ${seed.r} và ${seed.t}; tổng cộng 3 điểm.`,
      hint1: `Liệt kê các đoạn có chứa ${seed.q}.`,
      hint2: `${seed.p}–${seed.q}, ${seed.q}–${seed.r}, ${seed.q}–${seed.t}.`, estimatedSeconds: 65,
      variantGroup: 'logic-authored-graph-branch-v1',
    },
    {
      ...base,
      id: `VACT-LOGIC-AUTH-GRAPH-${seed.id}-03`, difficulty: 3, cognitiveLevel: 'M2', type: 'single-choice',
      stem: `Ít nhất phải đi qua bao nhiêu đường để đi từ ${seed.p} đến ${seed.s}?`,
      choices: [option('A', '2'), option('B', '3'), option('C', '4'), option('D', '5')], answer: 'B',
      solution: `Có hai đường ngắn cùng dài 3 đoạn: ${seed.p}–${seed.q}–${seed.r}–${seed.s} hoặc ${seed.p}–${seed.q}–${seed.t}–${seed.s}. Không có cạnh trực tiếp rút ngắn hơn.`,
      hint1: `Bắt đầu từ ${seed.p}; chỉ có một đường đầu tiên có thể đi.`,
      hint2: `Sau ${seed.p}–${seed.q}, có hai nhánh qua ${seed.r} hoặc ${seed.t}; mỗi nhánh cần thêm hai đoạn để đến ${seed.s}.`, estimatedSeconds: 100,
      variantGroup: 'logic-authored-graph-branch-v1',
    },
    {
      ...base,
      id: `VACT-LOGIC-AUTH-GRAPH-${seed.id}-04`, difficulty: 2, cognitiveLevel: 'M2', type: 'single-choice',
      stem: `Đường đi nào sau đây từ ${seed.p} đến ${seed.s} là hợp lệ?`,
      choices: [option('A', `${seed.p} → ${seed.q} → ${seed.t} → ${seed.s}`), option('B', `${seed.p} → ${seed.r} → ${seed.q} → ${seed.s}`), option('C', `${seed.p} → ${seed.q} → ${seed.s}`), option('D', `${seed.p} → ${seed.t} → ${seed.s}`)], answer: 'A',
      solution: `Các cạnh ${seed.p}–${seed.q}, ${seed.q}–${seed.t} và ${seed.t}–${seed.s} đều có trong sơ đồ. Những đường còn lại đều dùng ít nhất một cạnh không tồn tại.`,
      hint1: 'Kiểm tra từng cặp điểm liên tiếp trong mỗi phương án.',
      hint2: `${seed.p} chỉ nối trực tiếp với ${seed.q}; ${seed.q} nối trực tiếp với ${seed.t}; ${seed.t} nối trực tiếp với ${seed.s}.`, estimatedSeconds: 85,
      variantGroup: 'logic-authored-graph-branch-v1',
    },
  ]
}

const generatedOrderingSeeds = Array.from({ length: 10 }, (_, i) => generateOrderingSeed(`gen-ordering-${i + 1}`))
const generatedSelectionSeeds = Array.from({ length: 10 }, (_, i) => generateSelectionSeed(`gen-selection-${i + 1}`))
const generatedGraphSeeds = Array.from({ length: 10 }, (_, i) => generateGraphSeed(`gen-graph-${i + 1}`))

/**
 * Parameterized, original question families. Adding a new seed creates a
 * complete, reviewed mini-set instead of an isolated question with thin data.
 */
export const authoredLogicVariants: Question[] = [
  ...orderingSeeds.flatMap(orderingQuestions),
  ...selectionSeeds.flatMap(selectionQuestions),
  ...graphSeeds.flatMap(graphQuestions),
  ...generatedOrderingSeeds.flatMap(orderingQuestions),
  ...generatedSelectionSeeds.flatMap(selectionQuestions),
  ...generatedGraphSeeds.flatMap(graphQuestions),
]

/** Build a fresh, deterministic set of original parameterized logic items. */
export function createLogicVariantPack(seed: string): Question[] {
  return [
    ...orderingQuestions(generateOrderingSeed(`${seed}-ordering`)),
    ...selectionQuestions(generateSelectionSeed(`${seed}-selection`)),
    ...graphQuestions(generateGraphSeed(`${seed}-graph`)),
  ]
}
